import React, { useMemo } from 'react';
import Icon from 'components/AppIcon';

const METAL_COLORS = {
  'Cobre': { bg: 'rgba(234,88,12,0.12)', text: '#ea580c', border: 'rgba(234,88,12,0.25)' },
  'Aluminio': { bg: 'rgba(100,116,139,0.12)', text: '#64748b', border: 'rgba(100,116,139,0.25)' },
  'Plomo': { bg: 'rgba(71,85,105,0.15)', text: '#475569', border: 'rgba(71,85,105,0.3)' },
  'Zinc': { bg: 'rgba(59,130,246,0.12)', text: '#3b82f6', border: 'rgba(59,130,246,0.25)' },
  'Níquel': { bg: 'rgba(168,85,247,0.12)', text: '#a855f7', border: 'rgba(168,85,247,0.25)' },
  'Estaño': { bg: 'rgba(20,184,166,0.12)', text: '#14b8a6', border: 'rgba(20,184,166,0.25)' },
  'default': { bg: 'rgba(99,102,241,0.12)', text: '#6366f1', border: 'rgba(99,102,241,0.25)' },
};

function getMetalColor(metalName) {
  return METAL_COLORS?.[metalName] || METAL_COLORS?.['default'];
}

// Skeleton row matching the 7-column desktop table structure
function SkeletonTableRow() {
  return (
    <tr className="border-b border-border last:border-0">
      {/* Metal / Categoría */}
      <td className="px-4 py-3.5">
        <div className="flex items-center gap-2.5">
          <div className="h-5 w-16 bg-muted rounded-md animate-pulse" />
          <div className="h-4 w-24 bg-muted rounded animate-pulse" />
        </div>
      </td>
      {/* Nº Lotes */}
      <td className="px-4 py-3.5">
        <div className="h-4 w-6 bg-muted rounded animate-pulse" />
      </td>
      {/* Peso Disponible */}
      <td className="px-4 py-3.5">
        <div className="h-4 w-20 bg-muted rounded animate-pulse" />
      </td>
      {/* Yield Medio */}
      <td className="px-4 py-3.5">
        <div className="h-4 w-14 bg-muted rounded animate-pulse" />
      </td>
      {/* Benchmark */}
      <td className="px-4 py-3.5">
        <div className="h-4 w-20 bg-muted rounded animate-pulse" />
      </td>
      {/* Valor Medio Pagado */}
      <td className="px-4 py-3.5">
        <div className="h-4 w-20 bg-muted rounded animate-pulse" />
      </td>
      {/* Valor Recuperable */}
      <td className="px-4 py-3.5">
        <div className="h-4 w-24 bg-muted rounded animate-pulse" />
      </td>
      {/* Beneficio Teórico */}
      <td className="px-4 py-3.5">
        <div className="h-4 w-24 bg-muted rounded animate-pulse" />
      </td>
      {/* Action */}
      <td className="px-4 py-3.5">
        <div className="h-7 w-20 bg-muted rounded-lg animate-pulse" />
      </td>
    </tr>
  );
}

// Skeleton card matching the mobile card layout
function SkeletonMobileCard() {
  return (
    <div className="p-4">
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="h-5 w-16 bg-muted rounded-md animate-pulse" />
          <div className="h-4 w-24 bg-muted rounded animate-pulse" />
        </div>
        <div className="h-7 w-20 bg-muted rounded-lg animate-pulse ml-2" />
      </div>
      <div className="grid grid-cols-2 gap-2">
        {[1, 2, 3, 4]?.map(i => (
          <div key={i}>
            <div className="h-3 w-20 bg-muted rounded animate-pulse mb-1" />
            <div className="h-4 w-16 bg-muted rounded animate-pulse" />
          </div>
        ))}
      </div>
      <div className="mt-2 pt-2 border-t border-border">
        <div className="h-3 w-24 bg-muted rounded animate-pulse mb-1" />
        <div className="h-4 w-20 bg-muted rounded animate-pulse" />
      </div>
    </div>
  );
}

export default function AggregatedStockTable({ groups, benchmarks, loading, refreshing, onSell }) {
  const METAL_NAME_TO_SYMBOL = {
    'cobre': 'LME_CU',
    'aluminio': 'LME_AL',
    'zinc': 'LME_ZN',
    'plomo': 'LME_PB',
    'níquel': 'LME_NI',
    'niquel': 'LME_NI',
    'estaño': 'LME_SN',
    'estano': 'LME_SN',
    'acero inox': 'REG_INOX',
    'inox': 'REG_INOX',
    'hierro': 'REG_FE',
    'acero': 'REG_FE',
    'latón': 'REG_LAT',
    'laton': 'REG_LAT',
    'bronce': 'MAN_BRZ',
  };

  const benchmarkMap = useMemo(() => {
    const map = {};
    benchmarks?.forEach(b => { map[b.symbol] = parseFloat(b?.value) || 0; });
    return map;
  }, [benchmarks]);

  const getBenchmark = (metalName) => {
    if (!metalName || !benchmarks?.length) return null;
    const metalKey = metalName?.toLowerCase()?.trim();
    // 1. Exact match via explicit map
    const mappedSymbol = METAL_NAME_TO_SYMBOL?.[metalKey];
    if (mappedSymbol && benchmarkMap?.[mappedSymbol] != null) {
      return benchmarkMap?.[mappedSymbol] || null;
    }
    // 2. Match on group.benchmark_symbol if present (future-proof)
    // 3. Fallback: first benchmark whose symbol contains the first 3 chars of metalKey
    const entry = Object.entries(benchmarkMap)?.find(([k]) =>
      k?.toLowerCase()?.includes(metalKey?.slice(0, 3))
    );
    return entry ? entry?.[1] : null;
  };

  if (loading) {
    return (
      <div className="bg-card border border-border rounded-xl overflow-hidden">
        {/* Header skeleton */}
        <div className="p-5 border-b border-border flex items-center justify-between">
          <div>
            <div className="h-5 w-48 bg-muted rounded animate-pulse mb-1.5" />
            <div className="h-3 w-32 bg-muted rounded animate-pulse" />
          </div>
          <div className="h-6 w-28 bg-muted rounded-md animate-pulse" />
        </div>
        {/* Desktop skeleton rows */}
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border" style={{ background: 'var(--color-muted)' }}>
                {['Metal / Categoría', 'Nº Lotes', 'Peso Disponible', 'Yield Medio', 'Benchmark', 'Valor Medio Pagado', 'Valor Recuperable', 'Beneficio Teórico', '']?.map((h, i) => (
                  <th
                    key={i}
                    className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground"
                    style={{ fontFamily: '"IBM Plex Sans", sans-serif', letterSpacing: '0.04em', textTransform: 'uppercase' }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {[1, 2, 3, 4]?.map(i => <SkeletonTableRow key={i} />)}
            </tbody>
          </table>
        </div>
        {/* Mobile skeleton cards */}
        <div className="md:hidden divide-y divide-border">
          {[1, 2, 3]?.map(i => <SkeletonMobileCard key={i} />)}
        </div>
      </div>
    );
  }

  if (!groups?.length) {
    return (
      <div className="bg-card border border-border rounded-xl p-12 text-center">
        <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4" style={{ background: 'rgba(99,102,241,0.1)' }}>
          <Icon name="Package" size={26} color="var(--color-accent)" strokeWidth={1.5} />
        </div>
        <p className="font-semibold text-foreground mb-1" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>Sin stock disponible</p>
        <p className="text-sm text-muted-foreground" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>
          No hay lotes con peso disponible para venta en este momento.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-xl overflow-hidden relative">
      {/* Real-time refresh overlay — brief skeleton shimmer on top of existing rows */}
      {refreshing && (
        <div
          className="absolute inset-0 z-10 pointer-events-none"
          style={{ background: 'var(--color-card)', opacity: 0.55 }}
        >
          <div className="absolute inset-x-0 top-0 h-0.5 overflow-hidden rounded-t-xl">
            <div
              className="h-full animate-pulse"
              style={{
                background: 'linear-gradient(90deg, transparent 0%, var(--color-accent) 50%, transparent 100%)',
                backgroundSize: '200% 100%',
                animation: 'shimmer 1.2s infinite',
              }}
            />
          </div>
        </div>
      )}

      <div className="p-5 border-b border-border flex items-center justify-between">
        <div>
          <h2 className="font-semibold text-foreground" style={{ fontFamily: '"IBM Plex Sans", sans-serif', fontSize: '0.9375rem' }}>
            Stock Agregado por Categoría
          </h2>
          <p className="text-xs text-muted-foreground mt-0.5" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>
            {groups?.length} categoría{groups?.length !== 1 ? 's' : ''} con stock disponible
          </p>
        </div>
        <div className="flex items-center gap-2">
          {refreshing && (
            <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-md" style={{ background: 'rgba(99,102,241,0.1)' }}>
              <div className="w-1.5 h-1.5 rounded-full bg-accent animate-pulse" />
              <span className="text-xs font-medium" style={{ color: 'var(--color-accent)', fontFamily: '"IBM Plex Sans", sans-serif' }}>
                Actualizando…
              </span>
            </div>
          )}
          <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-muted">
            <Icon name="Info" size={12} color="var(--color-muted-foreground)" strokeWidth={2} />
            <span className="text-xs text-muted-foreground" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>FIFO automático</span>
          </div>
        </div>
      </div>

      {/* Desktop Table */}
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-border" style={{ background: 'var(--color-muted)' }}>
              {['Metal / Categoría', 'Nº Lotes', 'Peso Disponible', 'Yield Medio', 'Benchmark', 'Valor Medio Pagado', 'Valor Recuperable', 'Beneficio Teórico', '']?.map((h, i) => (
                <th
                  key={i}
                  className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground"
                  style={{ fontFamily: '"IBM Plex Sans", sans-serif', letterSpacing: '0.04em', textTransform: 'uppercase' }}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {refreshing
              ? [1, 2, 3, 4]?.map(i => <SkeletonTableRow key={i} />)
              : groups?.map((group, idx) => {
                  const benchmark = getBenchmark(group?.metal_name);
                  const avgYield = group?.avg_yield;
                  const recoverableValue = benchmark != null && avgYield != null
                    ? benchmark * (avgYield / 100) * group?.total_weight_remaining
                    : null;
                  const avgPurchasePrice = group?.avg_purchase_price;
                  const totalCost = avgPurchasePrice != null
                    ? avgPurchasePrice * group?.total_weight_remaining
                    : null;
                  const theoreticalProfit = recoverableValue != null && totalCost != null
                    ? recoverableValue - totalCost
                    : null;
                  const metalColor = getMetalColor(group?.metal_name);

                  return (
                    <tr
                      key={`${group?.metal_name}-${group?.category_name}-${idx}`}
                      className="border-b border-border last:border-0 hover:bg-muted/50 transition-colors"
                    >
                      <td className="px-4 py-3.5">
                        <div className="flex items-center gap-2.5">
                          <span
                            className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-semibold flex-shrink-0"
                            style={{ background: metalColor?.bg, color: metalColor?.text, border: `1px solid ${metalColor?.border}`, fontFamily: '"IBM Plex Sans", sans-serif' }}
                          >
                            {group?.metal_name}
                          </span>
                          <span className="text-sm font-medium text-foreground" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>
                            {group?.category_name}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3.5">
                        <span className="text-sm font-mono text-foreground" style={{ fontVariantNumeric: 'tabular-nums' }}>
                          {group?.batch_count}
                        </span>
                      </td>
                      <td className="px-4 py-3.5">
                        <span className="text-sm font-mono font-semibold text-foreground" style={{ fontVariantNumeric: 'tabular-nums' }}>
                          {group?.total_weight_remaining?.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 3 })} t
                        </span>
                      </td>
                      <td className="px-4 py-3.5">
                        <span className="text-sm font-mono text-foreground" style={{ fontVariantNumeric: 'tabular-nums' }}>
                          {avgYield != null ? `${avgYield?.toFixed(1)}%` : '—'}
                        </span>
                      </td>
                      <td className="px-4 py-3.5">
                        <span className="text-sm font-mono text-foreground" style={{ fontVariantNumeric: 'tabular-nums' }}>
                          {benchmark != null
                            ? `${benchmark?.toLocaleString('es-ES', { maximumFractionDigits: 0 })} €/t`
                            : <span className="text-muted-foreground">—</span>
                          }
                        </span>
                      </td>
                      <td className="px-4 py-3.5">
                        <span className="text-sm font-mono text-foreground" style={{ fontVariantNumeric: 'tabular-nums' }}>
                          {avgPurchasePrice != null
                            ? `${avgPurchasePrice?.toLocaleString('es-ES', { maximumFractionDigits: 0 })} €/t`
                            : <span className="text-muted-foreground">—</span>
                          }
                        </span>
                      </td>
                      <td className="px-4 py-3.5">
                        <span
                          className="text-sm font-mono font-semibold"
                          style={{ fontVariantNumeric: 'tabular-nums', color: recoverableValue != null ? 'var(--color-success)' : 'var(--color-muted-foreground)' }}
                        >
                          {recoverableValue != null
                            ? `${recoverableValue?.toLocaleString('es-ES', { maximumFractionDigits: 0 })} €`
                            : '—'
                          }
                        </span>
                      </td>
                      <td className="px-4 py-3.5">
                        {theoreticalProfit != null ? (
                          <span
                            className="text-sm font-mono font-semibold"
                            style={{
                              fontVariantNumeric: 'tabular-nums',
                              color: theoreticalProfit >= 0 ? 'var(--color-success)' : 'var(--color-destructive)',
                            }}
                          >
                            {theoreticalProfit >= 0 ? '+' : ''}{theoreticalProfit?.toLocaleString('es-ES', { maximumFractionDigits: 0 })} €
                          </span>
                        ) : (
                          <span className="text-sm text-muted-foreground">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3.5">
                        <button
                          onClick={() => onSell?.(group, benchmark, recoverableValue)}
                          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-all hover:opacity-90 active:scale-95"
                          style={{
                            background: 'var(--color-accent)',
                            color: '#fff',
                            fontFamily: '"IBM Plex Sans", sans-serif',
                            fontSize: '0.8125rem',
                          }}
                        >
                          <Icon name="ShoppingCart" size={13} color="#fff" strokeWidth={2.5} />
                          Vender
                        </button>
                      </td>
                    </tr>
                  );
                })
            }
          </tbody>
        </table>
      </div>

      {/* Mobile Cards */}
      <div className="md:hidden divide-y divide-border">
        {refreshing
          ? [1, 2, 3]?.map(i => <SkeletonMobileCard key={i} />)
          : groups?.map((group, idx) => {
              const benchmark = getBenchmark(group?.metal_name);
              const avgYield = group?.avg_yield;
              const recoverableValue = benchmark != null && avgYield != null
                ? benchmark * (avgYield / 100) * group?.total_weight_remaining
                : null;
              const avgPurchasePrice = group?.avg_purchase_price;
              const totalCost = avgPurchasePrice != null
                ? avgPurchasePrice * group?.total_weight_remaining
                : null;
              const theoreticalProfit = recoverableValue != null && totalCost != null
                ? recoverableValue - totalCost
                : null;
              const metalColor = getMetalColor(group?.metal_name);

              return (
                <div key={`${group?.metal_name}-${group?.category_name}-${idx}`} className="p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span
                        className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-semibold"
                        style={{ background: metalColor?.bg, color: metalColor?.text, border: `1px solid ${metalColor?.border}`, fontFamily: '"IBM Plex Sans", sans-serif' }}
                      >
                        {group?.metal_name}
                      </span>
                      <span className="text-sm font-medium text-foreground" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>
                        {group?.category_name}
                      </span>
                    </div>
                    <button
                      onClick={() => onSell?.(group, benchmark, recoverableValue)}
                      className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium flex-shrink-0 ml-2"
                      style={{ background: 'var(--color-accent)', color: '#fff', fontFamily: '"IBM Plex Sans", sans-serif' }}
                    >
                      <Icon name="ShoppingCart" size={12} color="#fff" strokeWidth={2.5} />
                      Vender
                    </button>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    {[
                      { label: 'Peso disponible', value: `${group?.total_weight_remaining?.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 3 })} t` },
                      { label: 'Lotes', value: group?.batch_count },
                      { label: 'Yield medio', value: avgYield != null ? `${avgYield?.toFixed(1)}%` : '—' },
                      { label: 'Benchmark', value: benchmark != null ? `${benchmark?.toLocaleString('es-ES', { maximumFractionDigits: 0 })} €/t` : '—' },
                      { label: 'Valor medio pagado', value: avgPurchasePrice != null ? `${avgPurchasePrice?.toLocaleString('es-ES', { maximumFractionDigits: 0 })} €/t` : '—' },
                    ]?.map(({ label, value }) => (
                      <div key={label}>
                        <p className="text-xs text-muted-foreground" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>{label}</p>
                        <p className="text-sm font-mono font-semibold text-foreground" style={{ fontVariantNumeric: 'tabular-nums' }}>{value}</p>
                      </div>
                    ))}
                  </div>
                  <div className="mt-2 pt-2 border-t border-border grid grid-cols-2 gap-2">
                    {recoverableValue != null && (
                      <div>
                        <p className="text-xs text-muted-foreground" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>Valor recuperable</p>
                        <p className="text-sm font-mono font-bold" style={{ color: 'var(--color-success)', fontVariantNumeric: 'tabular-nums' }}>
                          {recoverableValue?.toLocaleString('es-ES', { maximumFractionDigits: 0 })} €
                        </p>
                      </div>
                    )}
                    {theoreticalProfit != null && (
                      <div>
                        <p className="text-xs text-muted-foreground" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>Beneficio teórico</p>
                        <p
                          className="text-sm font-mono font-bold"
                          style={{
                            fontVariantNumeric: 'tabular-nums',
                            color: theoreticalProfit >= 0 ? 'var(--color-success)' : 'var(--color-destructive)',
                          }}
                        >
                          {theoreticalProfit >= 0 ? '+' : ''}{theoreticalProfit?.toLocaleString('es-ES', { maximumFractionDigits: 0 })} €
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              );
            })
        }
      </div>
    </div>
  );
}
